import React, { useState, useEffect, ChangeEvent, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { OrganizationsResponse, Organization } from '../types';
import './ManageOrganizations.css';

interface CreateOrganizationFormData {
  org_name: string;
  description: string;
  admin_data: {
    username: string;
    password: string;
    email: string;
  };
}

const ManageOrganizations: React.FC = () => {
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [modalError, setModalError] = useState<string>('');
  const [formData, setFormData] = useState<CreateOrganizationFormData>({
    org_name: '',
    description: '',
    admin_data: {
      username: '',
      password: '',
      email: ''
    }
  });
  const [isActivationModalOpen, setIsActivationModalOpen] = useState<boolean>(false);
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState<boolean>(false);

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const fetchOrganizations = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get<OrganizationsResponse>('/api/org/');
      const organizationsData = response.data?.data?.organizations;
      setOrganizations(Array.isArray(organizationsData) ? organizationsData : []);
    } catch (err: any) {
      console.error('Error fetching organizations:', err);
      setError(err.response?.data?.message || err.message || 'Failed to fetch organizations. Please try again.');
      setOrganizations([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const toggleUserStatus = async (userId: string, currentStatus: boolean, orgId: string) => {
    try {
      const newStatus = !currentStatus;
      await api.put(`/api/management/user/${userId}`, {
        is_active: newStatus
      });

      // Update the user in the local state immediately
      setOrganizations(prevOrgs =>
        prevOrgs.map(org => {
          if (org._id === orgId) {
            return {
              ...org,
              users: org.users.map(user =>
                user._id === userId
                  ? { ...user, is_active: newStatus, updated_at: new Date().toISOString() }
                  : user
              )
            };
          }
          return org;
        })
      );
    } catch (err: any) {
      console.error('Error updating user status:', err);
      const errorMessage = err.response?.data?.message || err.response?.data?.detail || err.message || 'Failed to update user status. Please try again.';
      setError(errorMessage);
      // Clear error after 5 seconds
      setTimeout(() => setError(null), 5000);
    }
  };

  const openActivationModal = (org: Organization) => {
    setSelectedOrg(org);
    setIsActivationModalOpen(true);
  };

  const closeActivationModal = () => {
    setIsActivationModalOpen(false);
    setSelectedOrg(null);
    setIsUpdatingStatus(false);
  };

  const toggleOrganizationStatus = async () => {
    if (!selectedOrg) return;

    try {
      setIsUpdatingStatus(true);
      const newStatus = !selectedOrg.is_active;
      await api.put(`/api/org/users/cleanup/${selectedOrg._id}`, {
        is_active: newStatus
      });

      // Update the organization in the local state
      setOrganizations(prevOrgs =>
        prevOrgs.map(org =>
          org._id === selectedOrg._id
            ? { ...org, is_active: newStatus, updated_at: new Date().toISOString() }
            : org
        )
      );

      // Also update all users in the organization
      setOrganizations(prevOrgs =>
        prevOrgs.map(org => {
          if (org._id === selectedOrg._id) {
            return {
              ...org,
              users: org.users.map(user => ({
                ...user,
                is_active: newStatus,
                updated_at: new Date().toISOString()
              }))
            };
          }
          return org;
        })
      );

      closeActivationModal();
    } catch (err: any) {
      console.error('Error updating organization status:', err);
      const errorMessage = err.response?.data?.message || err.response?.data?.detail || err.message || 'Failed to update organization status. Please try again.';
      setError(errorMessage);
      // Clear error after 5 seconds
      setTimeout(() => setError(null), 5000);
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const openModal = () => {
    setIsModalOpen(true);
    setFormData({
      org_name: '',
      description: '',
      admin_data: {
        username: '',
        password: '',
        email: ''
      }
    });
    setModalError('');
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setFormData({
      org_name: '',
      description: '',
      admin_data: {
        username: '',
        password: '',
        email: ''
      }
    });
    setModalError('');
    setIsSubmitting(false);
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    if (name.startsWith('admin_')) {
      const fieldName = name.replace('admin_', '');
      setFormData(prev => ({
        ...prev,
        admin_data: {
          ...prev.admin_data,
          [fieldName]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    // Clear error when user starts typing
    if (modalError) {
      setModalError('');
    }
  };

  const isFormValid = formData.org_name.trim() !== '' && 
                      formData.description.trim() !== '' &&
                      formData.admin_data.username.trim() !== '' &&
                      formData.admin_data.email.trim() !== '' &&
                      formData.admin_data.password.trim() !== '';

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setModalError('');

    try {
      // Create organization
      const response = await api.post('/api/org/register', {
        org_name: formData.org_name,
        description: formData.description,
        admin_data: {
          username: formData.admin_data.username,
          password: formData.admin_data.password,
          email: formData.admin_data.email
        }
      });

      // Get organization_id from response
      // Response structure: { status_code, message, data: { organization: { _id, ... }, admin: { ... } } }
      const organizationId = response.data?.data?.organization?._id;
      
      if (organizationId) {
        try {
          // Create secrets for the organization
          await api.post(`/api/user/creds/create/${organizationId}`);
        } catch (secretsErr: any) {
          // Log error but don't fail the whole operation
          console.error('Error creating secrets for organization:', secretsErr);
          // Organization was created successfully, so we continue
        }
      } else {
        console.warn('Organization ID not found in response, skipping secrets creation');
      }

      // Close modal and refresh organizations list
      closeModal();
      await fetchOrganizations();
    } catch (err: any) {
      console.error('Error creating organization:', err);
      setModalError(err.response?.data?.message || err.response?.data?.detail || err.message || 'Failed to create organization. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="manage-organizations-container">
      <div className="manage-organizations-header">
        <button className="back-button" onClick={() => navigate('/dashboard/migration')}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" fill="currentColor"/>
          </svg>
          Back
        </button>
        <h1>Manage Organizations</h1>
      </div>

      <div className="manage-organizations-content">
        <div className="organizations-header-actions">
          <button className="create-organization-button" onClick={openModal}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" fill="currentColor"/>
            </svg>
            Create Organization
          </button>
        </div>
        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading organizations...</p>
          </div>
        ) : error ? (
          <div className="error-container">
            <p className="error-message">{error}</p>
            <button className="retry-button" onClick={fetchOrganizations}>
              Retry
            </button>
          </div>
        ) : organizations.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" fill="currentColor"/>
              </svg>
            </div>
            <div className="empty-text">
              <div className="empty-title">No organizations found</div>
              <div className="empty-subtitle">There are no organizations available.</div>
            </div>
          </div>
        ) : (
          <div className="organizations-list">
            {organizations.map((org) => (
              <div key={org._id} className="organization-card">
                <div className="organization-header">
                  <div className="organization-info">
                    <div className="organization-name-row">
                      <h3 className="organization-name">{org.name}</h3>
                      <button
                        className="organization-status-toggle-button"
                        onClick={() => openActivationModal(org)}
                        title={org.is_active ? 'Deactivate organization' : 'Activate organization'}
                      >
                        {org.is_active ? 'Deactivate' : 'Activate'}
                      </button>
                    </div>
                    <p className="organization-description">{org.description}</p>
                    <div className="organization-meta">
                      <span className="meta-item">
                        <span className="meta-label">Created:</span> {formatDate(org.created_at)}
                      </span>
                      <span className="meta-item">
                        <span className="meta-label">Updated:</span> {formatDate(org.updated_at)}
                      </span>
                      <span className="meta-item">
                        <span className="meta-label">Status:</span>
                        <span className={`status-badge ${org.is_active ? 'active' : 'inactive'}`}>
                          {org.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </span>
                      <span className="meta-item">
                        <span className="meta-label">Total Users:</span> {org.user_count}
                      </span>
                    </div>
                  </div>
                </div>
                
                {org.users && org.users.length > 0 && (
                  <div className="organization-users">
                    <h4 className="users-section-title">Users ({org.users.length})</h4>
                    <div className="users-table-wrapper">
                      <table className="users-table">
                        <thead>
                          <tr className="table-header">
                            <th className="otable-header-cell">Username</th>
                            <th className="otable-header-cell">Email</th>
                            <th className="otable-header-cell">Role</th>
                            <th className="otable-header-cell">Status</th>
                            <th className="otable-header-cell">Created At</th>
                            <th className="otable-header-cell">Updated At</th>
                            <th className="otable-header-cell">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {org.users.map((user) => (
                            <tr key={user._id} className="table-row">
                              <td className="table-cell username-cell">{user.username}</td>
                              <td className="table-cell email-cell">{user.email}</td>
                              <td className="table-cell role-cell">
                                <span className={`role-badge role-${user.role.toLowerCase()}`}>
                                  {user.role}
                                </span>
                              </td>
                              <td className="table-cell status-cell">
                                <span className={`status-badge ${user.is_active ? 'active' : 'inactive'}`}>
                                  {user.is_active ? 'Active' : 'Inactive'}
                                </span>
                              </td>
                              <td className="table-cell date-cell">{formatDate(user.created_at)}</td>
                              <td className="table-cell date-cell">{formatDate(user.updated_at)}</td>
                              <td className="table-cell actions-cell">
                                <button
                                  className="status-toggle-button"
                                  onClick={() => toggleUserStatus(user._id, user.is_active, org._id)}
                                  title={user.is_active ? 'Deactivate user' : 'Activate user'}
                                  disabled={!org.is_active }
                                >
                                  {user.is_active ? 'Deactivate' : 'Activate'}
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {isModalOpen && (
        <div className="modal-overlay" role="dialog" aria-modal="true" onClick={(e) => {
          if (e.target === e.currentTarget) {
            closeModal();
          }
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Create New Organization</h3>
              <button className="modal-close" type="button" onClick={closeModal} aria-label="Close modal" disabled={isSubmitting}>
                ×
              </button>
            </div>
            <form className="modal-form" onSubmit={handleSubmit}>
              <div className="modal-field">
                <label htmlFor="org-name-input">Organization Name</label>
                <input
                  id="org-name-input"
                  name="org_name"
                  type="text"
                  value={formData.org_name}
                  onChange={handleInputChange}
                  placeholder="Enter organization name"
                  required
                  disabled={isSubmitting}
                />
              </div>
              <div className="modal-field">
                <label htmlFor="description-input">Description</label>
                <textarea
                  id="description-input"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Enter organization description"
                  required
                  disabled={isSubmitting}
                  rows={3}
                />
              </div>
              
              <div className="modal-section-divider">
                <h4>Admin Account</h4>
                <p className="modal-section-description">Create an admin account for this organization</p>
              </div>

              <div className="modal-field">
                <label htmlFor="admin-username-input">Admin Username</label>
                <input
                  id="admin-username-input"
                  name="admin_username"
                  type="text"
                  value={formData.admin_data.username}
                  onChange={handleInputChange}
                  placeholder="Enter admin username"
                  required
                  disabled={isSubmitting}
                />
              </div>
              <div className="modal-field">
                <label htmlFor="admin-email-input">Admin Email</label>
                <input
                  id="admin-email-input"
                  name="admin_email"
                  type="email"
                  value={formData.admin_data.email}
                  onChange={handleInputChange}
                  placeholder="Enter admin email"
                  required
                  disabled={isSubmitting}
                />
              </div>
              <div className="modal-field">
                <label htmlFor="admin-password-input">Admin Password</label>
                <input
                  id="admin-password-input"
                  name="admin_password"
                  type="password"
                  value={formData.admin_data.password}
                  onChange={handleInputChange}
                  placeholder="Enter admin password"
                  required
                  disabled={isSubmitting}
                />
              </div>

              {modalError && <div className="modal-error">{modalError}</div>}

              <div className="modal-actions">
                <button
                  type="button"
                  className="modal-secondary-button"
                  onClick={closeModal}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="modal-primary-button"
                  disabled={isSubmitting || !isFormValid}
                >
                  {isSubmitting ? 'Creating...' : 'Create Organization'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {isActivationModalOpen && selectedOrg && (
        <div className="modal-overlay" role="dialog" aria-modal="true" onClick={(e) => {
          if (e.target === e.currentTarget) {
            closeActivationModal();
          }
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Confirm Organization {selectedOrg.is_active ? 'Deactivation' : 'Activation'}</h3>
              <button className="modal-close" type="button" onClick={closeActivationModal} aria-label="Close modal" disabled={isUpdatingStatus}>
                ×
              </button>
            </div>
            <div className="modal-form">
              <div className="modal-field">
                <p style={{ margin: 0, fontSize: '14px', color: '#495057', lineHeight: '1.6' }}>
                  Are you sure you want to {selectedOrg.is_active ? 'deactivate' : 'activate'} the organization <strong>{selectedOrg.name}</strong>? 
                  This action will {selectedOrg.is_active ? 'deactivate' : 'activate'} all the users in the organization as well.
                </p>
              </div>

              {error && <div className="modal-error">{error}</div>}

              <div className="modal-actions">
                <button
                  type="button"
                  className="modal-secondary-button"
                  onClick={closeActivationModal}
                  disabled={isUpdatingStatus}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="modal-primary-button"
                  onClick={toggleOrganizationStatus}
                  disabled={isUpdatingStatus}
                >
                  {isUpdatingStatus ? 'Updating...' : 'Confirm'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageOrganizations;
